import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useLocationScope } from "@/lib/locationScope";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Plus, Pencil, Trash2, AlertTriangle, ShieldCheck, MapPin, Info, Users, Building2, CheckCircle2, Globe2 } from "lucide-react";
import { Modal } from "@/pages/Employees";
import Avatar from "@/components/Avatar";
import { toast } from "sonner";

const empty = { name: "", address: "", city: "", color: "#0033A0" };

export default function Locations() {
  const { t } = useI18n();
  const scope = useLocationScope();
  const [list, setList] = useState([]);
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = async () => {
    // Force unscoped fetch: the Locations page itself must always show every
    // location, even when the owner has zoomed in on one.
    const [locs, us, tm] = await Promise.all([
      api.get("/locations", { params: { __no_scope: true } }),
      api.get("/users",     { params: { __no_scope: true } }),
      api.get("/teams",     { params: { __no_scope: true } }),
    ]);
    setList(locs.data);
    setUsers(us.data);
    setTeams(tm.data);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShow(true); };
  const openEdit = (l) => { setEditing(l); setForm({ ...empty, ...l }); setShow(true); };
  const save = async () => {
    try {
      if (editing) await api.patch(`/locations/${editing._id}`, form);
      else await api.post("/locations", form);
      toast.success(t("saved")); setShow(false); load();
      window.dispatchEvent(new Event("rostr:locations-changed"));
    } catch { toast.error(t("error")); }
  };
  const del = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/locations/${confirmDel._id}`);
      toast.success(t("saved"));
      // If the deleted location was the active scope, clear it so the app
      // doesn't keep filtering to a ghost id.
      if (scope.id === confirmDel._id) scope.clear();
      setConfirmDel(null);
      load();
      window.dispatchEvent(new Event("rostr:locations-changed"));
    } catch (e) {
      toast.error(e.response?.data?.detail || t("error"));
    }
  };

  const activeLoc = list.find((l) => l._id === scope.id);

  return (
    <div data-testid="page-locations" className="flex flex-col gap-6">
      <PageHeader
        eyebrow={t("locations")}
        title={t("locations")}
        actions={<button className="btn btn-primary" onClick={openNew} data-testid="add-location"><Plus size={14} />{t("add_location")}</button>}
      />

      {/* Intro / how it works — Feb 2026: owners were confused about what
          "focussen op locatie" actually did across the app. Explain it here
          and always surface the current focus state. */}
      <div
        className="card p-5 md:p-6 flex flex-col md:flex-row gap-4 md:gap-6 border-l-[3px]"
        style={{ borderLeftColor: scope.id ? "hsl(var(--primary))" : "hsl(var(--text-soft))" }}
        data-testid="loc-intro-card"
      >
        <div className="shrink-0">
          <div className="w-11 h-11 rounded-xl grid place-items-center bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]">
            <Info size={18} strokeWidth={2} />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-[17px] font-bold text-[hsl(var(--text-strong))]">{t("loc_page_intro_title")}</div>
          <p className="text-[13.5px] leading-relaxed text-[hsl(var(--text-muted))] mt-1.5 max-w-3xl">
            {t("loc_page_intro_body")}
          </p>
          <div
            className="mt-3.5 inline-flex items-center gap-2.5 h-9 px-3.5 rounded-full text-[12.5px] font-semibold"
            style={{
              background: scope.id ? "hsl(var(--primary-soft))" : "hsl(var(--muted))",
              color: scope.id ? "hsl(var(--primary))" : "hsl(var(--text-strong))",
            }}
            data-testid="loc-current-focus-pill"
          >
            {scope.id
              ? <><MapPin size={13} strokeWidth={2.4} />{t("loc_page_intro_active").replace("{name}", activeLoc?.name || scope.name || "")}</>
              : <><Globe2 size={13} strokeWidth={2.2} />{t("loc_page_intro_all")}</>}
          </div>
        </div>
      </div>

      {list.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="loc-grid">
          {list.map((l) => (
            <LocationCard
              key={l._id}
              location={l}
              users={users}
              teams={teams}
              isActive={scope.id === l._id}
              onEdit={() => openEdit(l)}
              onDelete={() => setConfirmDel(l)}
              onFocus={(loc) => { scope.setScope(loc._id, loc.name); toast.success(t("loc_focus_activated").replace("{name}", loc.name)); }}
              onClearFocus={() => { scope.clear(); toast.success(t("loc_scope_cleared")); }}
              t={t}
            />
          ))}
        </div>
      )}

      {show && (
        <Modal title={editing ? t("edit") : t("add_location")} onClose={() => setShow(false)}>
          <div className="flex flex-col gap-3">
            <F label={t("name")}><input data-testid="loc-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} /></F>
            <F label={t("address")}><input value={form.address || ""} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} /></F>
            <F label={t("city")}><input value={form.city || ""} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inp} /></F>
            <F label={t("color")}><input type="color" value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} className="h-10 w-24 border hairline rounded-xl" /></F>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setShow(false)} className="btn btn-ghost">{t("cancel")}</button>
            <button onClick={save} className="btn btn-primary" data-testid="loc-save">{t("save")}</button>
          </div>
        </Modal>
      )}
      {confirmDel && (
        <Modal title={t("confirm_delete_title")} onClose={() => setConfirmDel(null)}>
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--danger-soft))] text-[hsl(var(--danger))] grid place-items-center shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div className="text-[13.5px] text-[hsl(var(--text-muted))] leading-relaxed">
              {t("confirm_delete_loc_body").replace("{name}", confirmDel.name)}
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setConfirmDel(null)} className="btn btn-ghost" data-testid="del-loc-cancel">{t("cancel")}</button>
            <button onClick={del} className="btn btn-primary !bg-[hsl(var(--danger))]" data-testid="del-loc-confirm">{t("delete")}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
const inp = "card h-10 px-3 text-sm rounded-xl w-full";
function F({ label, children }) { return (<label className="flex flex-col gap-1"><span className="eyebrow">{label}</span>{children}</label>); }

// Location card — richer layout with employee/team counts, manager avatars,
// clear "Bekijk deze locatie" primary action, and an "active" state when
// this location is the current focus.
function LocationCard({ location, users, teams, isActive, onEdit, onDelete, onFocus, onClearFocus, t }) {
  const assigned = useMemo(() => {
    const mgmt = users.filter((u) => u.role === "manager" || u.role === "teamlead");
    const scoped = mgmt.filter((u) => Array.isArray(u.managed_location_ids) && u.managed_location_ids.length > 0);
    const unscoped = mgmt.filter((u) => !u.managed_location_ids || u.managed_location_ids.length === 0);
    const here = scoped.filter((u) => u.managed_location_ids.includes(location._id));
    return { here, unscoped };
  }, [users, location._id]);

  const empCount = users.filter((u) => u.location_id === location._id).length;
  const teamCount = teams.filter((tm) => tm.location_id === location._id).length;
  const hasAssigned = assigned.here.length > 0 || assigned.unscoped.length > 0;

  return (
    <div
      className={`card p-5 flex flex-col gap-4 transition-shadow ${isActive ? "ring-2 ring-[hsl(var(--primary))]" : "hover:shadow-[var(--shadow-md)]"}`}
      data-testid={`loc-${location._id}`}
    >
      {/* Header: color, name, address, edit/delete */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div
            className="w-10 h-10 rounded-xl shrink-0 grid place-items-center"
            style={{ background: (location.color || "#0033A0") + "22" }}
          >
            <span className="w-4 h-4 rounded-md" style={{ background: location.color || "#0033A0" }} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-display text-lg font-bold text-[hsl(var(--text-strong))] leading-tight">{location.name}</h3>
              {isActive && (
                <span
                  className="inline-flex items-center gap-1 h-5 px-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]"
                  data-testid={`loc-active-${location._id}`}
                >
                  <CheckCircle2 size={10} strokeWidth={2.4} />
                  {t("loc_active_here")}
                </span>
              )}
            </div>
            <div className="text-[12.5px] text-[hsl(var(--text-muted))] mt-1 truncate">
              {[location.address, location.city].filter(Boolean).join(", ") || "—"}
            </div>
          </div>
        </div>
        <div className="flex gap-1 shrink-0">
          <button className="btn btn-ghost !h-8 !px-2" onClick={onEdit} data-testid={`edit-loc-${location._id}`} title={t("edit")}><Pencil size={12} /></button>
          <button className="btn btn-ghost !h-8 !px-2 hover:!text-[hsl(var(--danger))]" onClick={onDelete} data-testid={`del-loc-${location._id}`} title={t("delete")}><Trash2 size={12} /></button>
        </div>
      </div>

      {/* Stats row */}
      <div className="flex items-center gap-3 text-[12px]">
        <div className="flex items-center gap-1.5 text-[hsl(var(--text-muted))]">
          <Users size={13} strokeWidth={2} />
          <span className="tabular-nums font-bold text-[hsl(var(--text-strong))]">{empCount}</span>
          <span>{t("loc_stat_employees")}</span>
        </div>
        <span className="w-px h-3 bg-[hsl(var(--border))]" />
        <div className="flex items-center gap-1.5 text-[hsl(var(--text-muted))]">
          <Building2 size={13} strokeWidth={2} />
          <span className="tabular-nums font-bold text-[hsl(var(--text-strong))]">{teamCount}</span>
          <span>{t("loc_stat_teams")}</span>
        </div>
      </div>

      {/* Managers row */}
      <div className="pt-3 border-t hairline" data-testid={`loc-managers-${location._id}`}>
        <div className="eyebrow flex items-center gap-1.5 mb-2">
          <ShieldCheck size={11} strokeWidth={2} />
          {t("loc_managers_label")}
        </div>
        {!hasAssigned ? (
          <div className="text-[11.5px] text-[hsl(var(--text-muted))] italic">
            {t("loc_managers_none")}
          </div>
        ) : (
          <div className="flex items-center gap-2.5" data-testid={`loc-mgr-row-${location._id}`}>
            <div className="flex -space-x-2.5">
              {[...assigned.here, ...assigned.unscoped].slice(0, 6).map((u) => (
                <Avatar
                  key={u._id}
                  user={u}
                  size={28}
                  ring
                  title={`${u.name} (${t(`role_${u.role}`)})${!u.managed_location_ids || u.managed_location_ids.length === 0 ? " · " + t("loc_managers_unscoped_tag") : ""}`}
                  testid={`loc-mgr-${location._id}-${u._id}`}
                />
              ))}
              {(assigned.here.length + assigned.unscoped.length) > 6 && (
                <span
                  className="w-[28px] h-[28px] rounded-full grid place-items-center text-[10px] font-bold bg-[hsl(var(--muted))] text-[hsl(var(--text-strong))] ring-2 ring-[hsl(var(--surface))]"
                  title={t("loc_managers_more_tooltip") || "En nog meer"}
                >
                  +{(assigned.here.length + assigned.unscoped.length) - 6}
                </span>
              )}
            </div>
            <div className="text-[11.5px] text-[hsl(var(--text-muted))] leading-tight min-w-0 flex-1">
              <span className="font-medium text-[hsl(var(--text))] truncate block">
                {[...assigned.here, ...assigned.unscoped].slice(0, 2).map((u) => u.name.split(" ")[0]).join(", ")}
                {(assigned.here.length + assigned.unscoped.length) > 2 && ` +${(assigned.here.length + assigned.unscoped.length) - 2}`}
              </span>
              {assigned.unscoped.length > 0 && (
                <span className="text-[10px] opacity-80">
                  {assigned.unscoped.length} · {t("loc_managers_unscoped_tag")}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="pt-2 flex gap-2">
        {isActive ? (
          <button
            type="button"
            onClick={onClearFocus}
            data-testid={`loc-clear-${location._id}`}
            className="btn btn-ghost flex-1 !h-10"
          >
            <Globe2 size={13} strokeWidth={2.2} />
            {t("loc_scope_switcher_all")}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => onFocus?.(location)}
            data-testid={`loc-focus-${location._id}`}
            className="btn btn-primary flex-1 !h-10"
          >
            <MapPin size={13} strokeWidth={2.2} />
            {t("loc_view_this")}
          </button>
        )}
      </div>
    </div>
  );
}
